// src/auth/auth-store.ts
import { create } from 'zustand';
import type { AuthStatus, UserData } from './types';
import { authStorage } from './storage';
import { routeOwnerStorage, currentPath } from './route-owner';
import { decodeJwt, isExpired } from './jwt';
import {
  passwordGrant,
  refreshGrant,
  fetchUserData,
  updateDeviceDetails,
  ActiveSessionError,
} from './token-service';
import { DEV_AUTO_LOGIN_ENABLED } from './dev-auto-login';
import { DEV_PERSONA_LOGIN } from './dev-persona-login';

/** Options for {@link AuthState.login}. */
export interface LoginOptions {
  /** On a 409 "User already logged in", recover by taking over the prior session
   *  (UpdateDeviceDetails → re-login) instead of degrading to JWT-only (the
   *  PROD-1298 symptom: onboarding gate fires + empty practice selector). In dev
   *  the takeover is automatic; on the deployed manual-login path it surfaces the
   *  "Ongoing Session" confirm dialog first. The old SkipConflict header this
   *  once relied on is a no-op server-side (Identity PROD-931). */
  recoverActiveSession?: boolean;
  /** Take over a 409 automatically, with no dialog, as local dev does. Set only
   *  by the deployed-dev `?devlogin` persona sign-in (dev-persona-login.ts), where
   *  no one is at the keyboard to confirm. */
  autoTakeover?: boolean;
  /** DEV-PERSONA ONLY (App.tsx persona branch, never a person's own sign-in). An explicit persona request for
   *  THIS url is not a route inherited from the previous person, so `routeOwnerChanged` is forced to false and
   *  the deep link (`/encounter/<id>?login=hassan`) stays. `sessionOwnerChanged` is still computed, so the
   *  previous persona's leftovers are cleared. */
  keepRoute?: boolean;
}

interface AuthState {
  status: AuthStatus;
  accessToken: string | null;
  refreshToken: string | null;
  user: UserData | null;
  login: (username: string, password: string, opts?: LoginOptions) => Promise<void>;
  logout: () => void;
  /** DEV-PERSONA ONLY (App.tsx persona branch). Same as `logout()` but writes neither the route-owner ledger page
   *  nor the tab-end marker. A separate action, not an argument to `logout`, because `logout` is passed as an
   *  onClick handler and would receive a MouseEvent. Pair it with `login(..., { keepRoute: true })`. */
  endSessionForPersonaSwitch: () => void;
  /** Non-null while the active-session confirm dialog is open (deployed 409
   *  path). `userId` is the session being taken over (the same user). */
  activeSessionConflict: { userId: string } | null;
  /** User confirmed the "Ongoing Session" dialog — clear the prior session
   *  (UpdateDeviceDetails) and re-auth. Resolves the parked login() promise. */
  confirmTakeover: () => Promise<void>;
  /** User declined the dialog — reject the parked login() promise + log out. */
  cancelTakeover: () => void;
  /** Returns a non-expired access token, refreshing proactively if needed. */
  getValidAccessToken: () => Promise<string>;
  /** Force a refresh (called reactively by the api-client on 401). */
  forceRefresh: () => Promise<string>;
  /** Re-hydrate session from localStorage on boot. */
  hydrate: () => void;
  markOnboardingComplete: () => void;
  /** True from a sign-in by a DIFFERENT person than the one whose session ended on this very page, until the
   *  route gate has moved the app off that URL (protected-route.tsx). */
  routeOwnerChanged: boolean;
  /** True after a sign-in by a different person than the one whose session ended on this browser, whatever
   *  page the sign-in is on. Drives the clean-up of the earlier person's leftovers (session-cleanup.ts). */
  sessionOwnerChanged: boolean;
  acknowledgeRouteOwnerChanged: () => void;
  /**
   * Set the active practice (the calendar PracticeSwitcher). Mirrors PM
   * `auth-store.setActivePractice` (PROD-1190) — updates `user.practiceId` /
   * `user.practiceName` and persists, so the api-client's PracticeId header
   * and every `user.practiceId` selector re-key. Returns false on no-op.
   */
  setActivePractice: (practice: {
    practiceSetupId: string;
    practiceName: string | null;
  }) => boolean;
}

let refreshInFlight: Promise<string> | null = null;

/**
 * Parked context while the deployed active-session confirm dialog is open.
 * The 409 login() promise stays pending until confirmTakeover / cancelTakeover
 * settles it. Only one conflict can be pending at a time (the dialog is modal).
 */
let pendingConflict:
  | {
      accessToken: string;
      username: string;
      password: string;
      userId: string;
      resolve: () => void;
      reject: (e: unknown) => void;
    }
  | null = null;

/**
 * Bumped on every logout/login transition. An in-flight background profile
 * refresh (kicked off from `hydrate`) captures this value before it starts;
 * if a logout + login-as-another-user happens while it's still in flight, the
 * captured value no longer matches when the refresh resolves and the result
 * is discarded instead of being installed over the new session's profile
 * (P1: overlapping refresh must never leak the OLD user's profile onto the
 * NEW session's tokens).
 */
let sessionEpoch = 0;

/**
 * True when `accessToken` was issued to the person `user` describes (JWT `sub` ===
 * `user.userId`). Tokens and the profile live in localStorage, which every tab of the
 * origin shares, while each tab holds its own session in memory. A token and a profile
 * that came from two different sign-ins must never be paired: the backend reads the
 * caller from the token and the app sends ids from the profile, so a mixed pair acts as
 * neither person (PM refuses e.g. CreateUpdateMacro: "A phrase can only be created for
 * yourself").
 */
function tokenBelongsTo(accessToken: string, user: UserData): boolean {
  return sameIdentity(decodeJwt(accessToken)?.sub, user.userId);
}

/**
 * Identity ids are GUIDs; compare them trimmed and case-insensitively. Missing or empty
 * never matches: every stored profile carries `userId` (token-service `fetchUserData`
 * has always set it from the token `sub`, since the first version), so a profile
 * without one is not a profile this app wrote.
 */
function sameIdentity(a: string | null | undefined, b: string | null | undefined): boolean {
  const norm = (v: string | null | undefined) => (v ?? '').trim().toLowerCase();
  return norm(a) !== '' && norm(a) === norm(b);
}

/** The session of `userId` ends on this page (route-owner.ts). ProtectedRoute adds pages while signed in. */
function markRouteOwner(userId: string | null | undefined): void {
  if (!userId) return;
  const path = currentPath();
  routeOwnerStorage.addPage(userId, path);
  routeOwnerStorage.setTabEnd(userId, path);
}

/**
 * Compare a sign-in by `userId` on this page with the route-owner ledger, and make `userId` the last person.
 * Runs inside login / confirmTakeover, in the same update that flips to `authenticated`. No ledger (fresh
 * browser) is no change. Another person's leftovers are cleared whatever the page (`sessionOwnerChanged`); the
 * URL changes only when another person visited this very page (`routeOwnerChanged`).
 */
function takeOwnerChange(userId: string): { routeOwnerChanged: boolean; sessionOwnerChanged: boolean } {
  return routeOwnerStorage.signIn(userId, currentPath());
}

/** Ends the session. `markRoute` false (dev persona switch only) skips the ledger page and the tab-end marker. */
function endSession(markRoute: boolean): void {
  const { getState, setState } = useAuthStore;
  // New session boundary — see login() / sessionEpoch above.
  sessionEpoch++;
  // The session ends on this page: add it to the person's pages in the route-owner ledger.
  if (markRoute) markRouteOwner(getState().user?.userId);
  authStorage.clear();
  refreshInFlight = null;
  // Bail any conflict dialog still open (e.g. a force-logout while it shows)
  // so its parked login() promise never dangles.
  if (pendingConflict) {
    pendingConflict.reject(new Error('Session cleared'));
    pendingConflict = null;
  }
  setState({
    status: 'unauthenticated',
    accessToken: null,
    refreshToken: null,
    user: null,
    activeSessionConflict: null,
    routeOwnerChanged: false,
    sessionOwnerChanged: false,
  });
}

export const useAuthStore = create<AuthState>((set, get) => ({
  routeOwnerChanged: false,
  sessionOwnerChanged: false,
  acknowledgeRouteOwnerChanged: () => set({ routeOwnerChanged: false }),
  status: 'unknown',
  accessToken: null,
  refreshToken: null,
  user: null,
  activeSessionConflict: null,

  hydrate: () => {
    const accessToken = authStorage.getAccessToken();
    const refreshToken = authStorage.getRefreshToken();
    const user = authStorage.getUser();
    // A pair from two different identities (one tab refreshed its own tokens over another
    // tab's newer sign-in) is not a session: it falls to the clear below, and the normal
    // sign-in flow (login page, dev persona / auto-login) starts fresh.
    if (accessToken && user && tokenBelongsTo(accessToken, user)) {
      set({ accessToken, refreshToken, user, status: 'authenticated' });
      // A profile cached by an older build can lack newly-read GetUserDataToken fields that now gate
      // behaviour (e.g. `medicationSystem`, which the drug-interaction Vidal gate reads). Trusting
      // that stale cache can flip a safety gate the wrong way, so refetch the profile in the
      // background when its schema is out of date. Best-effort: a failure leaves the cached profile
      // in place and never blocks boot.
      if (authStorage.isUserSchemaStale()) {
        const startEpoch = sessionEpoch;
        void (async () => {
          try {
            const token = await get().getValidAccessToken();
            const fresh = await fetchUserData(token);
            // The session changed (logout, or logout+login-as-another-user) while
            // this was in flight — the token above belonged to the OLD session.
            // Discard silently rather than install a stale-session profile onto
            // whatever session is current now.
            if (sessionEpoch !== startEpoch) return;
            // Persist only next to a stored token of the same person. Another tab may
            // have signed in as someone else meanwhile (this tab's refresh then keeps
            // its tokens in memory only, see forceRefresh); writing this profile there
            // would pair that person's token with this person's profile.
            if (tokenBelongsTo(authStorage.getAccessToken() ?? '', fresh)) {
              authStorage.setUser(fresh);
            }
            set({ user: fresh });
          } catch {
            // GetUserDataToken failed (ProfileUnavailableError) or the refresh did.
            // This user already HAS a full cached profile, so unlike login there is
            // nothing degraded to install — keep the cached one, and do NOT stamp
            // the schema version, so a real retry happens on the next hydrate.
            // Deliberately does not log the user out: a background staleness
            // refresh is best-effort and must never interrupt a working session.
          }
        })();
      }
    } else {
      // Never leave HALF a session on disk. `hydrate` only trusts token+profile
      // together, so anything else here (a token whose profile failed to persist,
      // a profile whose token was evicted, a token and profile of two different
      // people) is unusable — and an orphan access
      // token is a live credential nothing is going to clear. Drop the lot, so
      // the only two states on disk are "complete session" and "nothing".
      if (accessToken || refreshToken || user) {
        // Tokens that expired while the tab or browser was closed. The URL this tab OPENED on is not a page
        // this person was on, so no page is added; only name them, so their leftovers are cleared later.
        if (user) routeOwnerStorage.noteStaleUser(user.userId);
        authStorage.clear();
      }
      set({ status: 'unauthenticated' });
    }
  },

  login: async (username, password, opts) => {
    // New session boundary — invalidates any background profile refresh still
    // in flight from a previous session (see hydrate's sessionEpoch check).
    sessionEpoch++;
    const autoTakeover = DEV_AUTO_LOGIN_ENABLED || (opts?.autoTakeover ?? false);
    let tokens = await passwordGrant(username, password);
    let user: UserData;
    try {
      user = await fetchUserData(tokens.accessToken, {
        // In local dev we WANT to surface the 409 so the auto-takeover below can
        // handle it (the SkipConflict recovery is a no-op server-side).
        throwOnActiveSession:
          (opts?.recoverActiveSession ?? false) || autoTakeover,
      });
    } catch (e) {
      // Recovery from a 409 is an active-session takeover. The backend
      // `SkipConflict` header was removed (Identity PROD-931,
      // GenerateUserTokenDataCommandHandler.cs), so the prior `skipConflict`
      // retry was a no-op that silently degraded to JWT-only (the PROD-1298
      // symptom, PROD-1727). The only real clear is UpdateDeviceDetails →
      // IsUserLoggedIn=false (AccountController.cs:719); it releases the user's
      // OWN prior session (the lock is per-user), never another clinician's.
      if (
        !(e instanceof ActiveSessionError) ||
        !(opts?.recoverActiveSession || autoTakeover)
      ) {
        // Includes ProfileUnavailableError: GetUserDataToken did not hand back a
        // profile, so there is no `role`/`tenantRoleId` and this must NOT become
        // an authenticated session — the user goes back to the login flow instead
        // (login-page renders the re-auth message off this rejection). Nothing has
        // been written to authStorage yet at this point; logout() is what clears
        // any EARLIER session's tokens/profile still sitting there, so a failed
        // sign-in can never leave the previous user half-installed.
        get().logout();
        throw e;
      }
      const userId = decodeJwt(tokens.accessToken)?.sub ?? '';
      if (autoTakeover) {
        // Dev: take over automatically (no dialog) so an agent/engineer isn't
        // blocked by a stale session. Never silent — log it.
        console.warn(
          '[dev-auto-login] active-session 409 — taking over the prior session for user',
          userId,
        );
        try {
          await updateDeviceDetails(tokens.accessToken, userId);
          tokens = await passwordGrant(username, password);
          user = await fetchUserData(tokens.accessToken, {
            throwOnActiveSession: false,
          });
        } catch (retryError) {
          // The takeover, the re-grant or the retried profile fetch failed. Same
          // rule as above: no profile ⇒ no session. Clear and surface.
          get().logout();
          throw retryError;
        }
      } else {
        // Deployed: park and surface the "Ongoing Session" confirm dialog. The
        // login() promise stays pending until the user decides — confirmTakeover
        // finalizes + resolves, cancelTakeover rejects. Mirrors the PM app's
        // ActiveSessionConflictDialog (user consents before the other session is
        // released).
        await new Promise<void>((resolve, reject) => {
          pendingConflict = {
            accessToken: tokens.accessToken,
            username,
            password,
            userId,
            resolve,
            reject,
          };
          set({ activeSessionConflict: { userId } });
        });
        return; // confirmTakeover already finalized the session.
      }
    }
    // Reached only with a real profile in hand — tokens and profile are written
    // together, so the persisted session is never half a session.
    authStorage.setAccessToken(tokens.accessToken);
    authStorage.setRefreshToken(tokens.refreshToken);
    authStorage.setUser(user);
    // Decided in the same update that flips to `authenticated`, so the route gate never renders the
    // previous person's URL for this person, not even for one frame.
    const taken = takeOwnerChange(user.userId);
    // Dev persona only (honoured only in a persona build, never in a person's own sign-in): an explicit
    // request for this URL is not an inherited route.
    const keep = opts?.keepRoute === true && DEV_PERSONA_LOGIN !== null;
    const ownerChange = { ...taken, routeOwnerChanged: keep ? false : taken.routeOwnerChanged };
    set({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      user,
      status: 'authenticated',
      activeSessionConflict: null,
      ...ownerChange,
    });
  },

  confirmTakeover: async () => {
    const ctx = pendingConflict;
    if (!ctx) return;
    // Dismiss the dialog immediately; the module-local ctx drives the retry.
    set({ activeSessionConflict: null });
    try {
      // Clear the prior session server-side, then re-issue /connect/token and
      // re-fetch the full profile (the user-confirmed analog of the dev takeover).
      await updateDeviceDetails(ctx.accessToken, ctx.userId);
      const fresh = await passwordGrant(ctx.username, ctx.password);
      // Throws ProfileUnavailableError if the profile does not load, which the
      // catch below turns into a rejected login() + logout(). The previous
      // `const { user } = ...` discarded the degraded-profile flag outright, so
      // this path authenticated a roleless user more silently than login() did.
      const user = await fetchUserData(fresh.accessToken, {
        throwOnActiveSession: false,
      });
      authStorage.setAccessToken(fresh.accessToken);
      authStorage.setRefreshToken(fresh.refreshToken);
      authStorage.setUser(user);
      const ownerChange = takeOwnerChange(user.userId);
      set({
        accessToken: fresh.accessToken,
        refreshToken: fresh.refreshToken,
        user,
        status: 'authenticated',
        ...ownerChange,
      });
      pendingConflict = null;
      ctx.resolve();
    } catch (err) {
      // Takeover failed mid-retry, or the profile did not load — surface to the
      // awaiting login() promise and bounce to a clean unauthenticated state
      // (logout() clears authStorage, so no tokens survive without a profile).
      pendingConflict = null;
      ctx.reject(err);
      get().logout();
    }
  },

  cancelTakeover: () => {
    const ctx = pendingConflict;
    pendingConflict = null;
    set({ activeSessionConflict: null });
    if (ctx) ctx.reject(new Error('Active session takeover cancelled by user'));
    get().logout();
  },

  logout: () => endSession(true),

  // Without a persona (every normal build) this is exactly logout(): the route is marked.
  endSessionForPersonaSwitch: () => endSession(DEV_PERSONA_LOGIN === null),

  getValidAccessToken: async () => {
    const { accessToken } = get();
    if (accessToken && !isExpired(accessToken, 30)) return accessToken;
    return get().forceRefresh();
  },

  forceRefresh: async () => {
    if (refreshInFlight) return refreshInFlight;
    const { refreshToken } = get();
    if (!refreshToken) {
      get().logout();
      throw new Error('no refresh token');
    }
    refreshInFlight = (async () => {
      try {
        const tokens = await refreshGrant(refreshToken);
        // Write to the shared storage only while it still holds THIS tab's person.
        // If another tab has since signed in as someone else (or signed out), the
        // stored profile is no longer ours: writing our tokens next to it would pair
        // one person's token with another person's profile for every tab that
        // hydrates later. Keep the refreshed tokens in memory only — this tab's own
        // token and profile still match, so it keeps working, and the other tab's
        // stored session is left untouched.
        const stored = authStorage.getUser();
        const mine = get().user;
        if (stored && mine && sameIdentity(stored.userId, mine.userId)) {
          authStorage.setAccessToken(tokens.accessToken);
          authStorage.setRefreshToken(tokens.refreshToken);
        }
        set({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken });
        return tokens.accessToken;
      } catch (e) {
        get().logout();
        throw e;
      } finally {
        refreshInFlight = null;
      }
    })();
    return refreshInFlight;
  },

  markOnboardingComplete: () => {
    const { user } = get();
    if (!user) return;
    const next = { ...user, isOnboarded: true };
    authStorage.setUser(next);
    set({ user: next });
  },

  setActivePractice: ({ practiceSetupId, practiceName }) => {
    const current = get().user;
    if (!current || !practiceSetupId) return false;
    if (current.practiceId === practiceSetupId) return false;
    const next: UserData = {
      ...current,
      practiceId: practiceSetupId,
      practiceName: practiceName ?? undefined,
    };
    authStorage.setUser(next);
    set({ user: next });
    return true;
  },
}));
